import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Download, Lock, Sparkles, ArrowUpRight } from "lucide-react";
import { api, IMAGES, CATEGORY_LABELS } from "../api";
import { Marquee } from "../components/Marquee";
import { ProductCard } from "../components/ProductCard";
import { Reveal } from "../components/Reveal";

const HERO_LINES = ["Mindful planning,", "beautifully", "delivered."];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const BENTO = [
  { id: "study", img: IMAGES.study, cls: "md:col-span-7 md:row-span-2 aspect-[4/3] md:aspect-auto", testid: "bento-study" },
  { id: "budget", img: IMAGES.budget, cls: "md:col-span-5 aspect-[4/3]", testid: "bento-budget" },
  { id: "habits", img: IMAGES.habits, cls: "md:col-span-5 aspect-[4/3]", testid: "bento-habits" },
  { id: "templates", img: IMAGES.templates, cls: "md:col-span-12 aspect-[16/7]", testid: "bento-templates" },
];

const STEPS = [
  { icon: Sparkles, title: "Choose your system", text: "Browse original planners, trackers and templates designed slowly and deliberately." },
  { icon: Lock, title: "Secure checkout", text: "Pay safely with Stripe — cards, Apple Pay and Google Pay accepted internationally." },
  { icon: Download, title: "Instant download", text: "Your files arrive the moment payment is confirmed, with a receipt in your inbox." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } })
      .then((r) => setFeatured(r.data.products.slice(0, 4)))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="homepage">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden">
        <motion.div
          style={{ y: glowY }}
          className="pointer-events-none absolute -right-32 top-10 h-[28rem] w-[28rem] rounded-full bg-lavender-tint blur-3xl"
        />
        <div className="pointer-events-none absolute -left-40 bottom-0 h-[24rem] w-[24rem] rounded-full bg-sage-tint blur-3xl" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 pb-20 pt-16 md:px-8 lg:grid-cols-12 lg:pb-28 lg:pt-24">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="font-mono text-[11px] uppercase tracking-[0.3em] text-sage"
            >
              Bloom Paper Studio · Digital Downloads
            </motion.p>
            <h1
              data-testid="hero-headline"
              className="mt-6 font-serif text-5xl font-light leading-[1.05] tracking-tight text-ink sm:text-6xl lg:text-7xl"
            >
              {HERO_LINES.map((line, i) => (
                <MaskedLine key={line} delay={0.25 + i * 0.14}>
                  {i === 1 ? <em className="text-lavender-deep">{line}</em> : line}
                </MaskedLine>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
              className="mt-7 max-w-md text-base font-light leading-relaxed text-ink/65 sm:text-lg"
            >
              Original study planners, budget planners, habit trackers and editable templates —
              made on paper first, delivered to you in seconds.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95, duration: 0.8 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/shop"
                data-testid="hero-shop-now-button"
                className="group inline-flex items-center gap-3 rounded-full bg-sage px-8 py-4 text-sm font-medium tracking-wide text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-sage-deep hover:shadow-[0_18px_35px_-12px_rgba(59,87,75,0.5)]"
              >
                Shop Now
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/about"
                data-testid="hero-about-link"
                className="rounded-full border border-ink/15 px-8 py-4 text-sm tracking-wide text-ink/80 transition-all duration-300 hover:-translate-y-0.5 hover:border-lavender hover:text-lavender-deep"
              >
                Our Story
              </Link>
            </motion.div>
          </div>
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, rotate: 2 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <motion.div style={{ y: imgY }} className="overflow-hidden rounded-[2rem] border border-sage/15 shadow-[0_30px_60px_-20px_rgba(59,87,75,0.3)]">
                <img src={IMAGES.heroFlatlay} alt="Elegant planner flat-lay" className="aspect-[4/5] w-full object-cover" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.8 }}
                className="absolute -bottom-6 -left-6 rounded-2xl border border-lavender/25 bg-white/85 px-5 py-4 shadow-lg backdrop-blur-md"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-lavender-deep">Instant delivery</p>
                <p className="mt-1 font-serif text-lg text-ink">Yours in under a minute</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* FEATURED */}
      <section className="mx-auto max-w-7xl px-6 py-20 md:px-8 lg:py-28" data-testid="featured-section">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sage">Signature pieces</p>
              <h2 className="mt-3 font-serif text-3xl font-normal tracking-tight text-ink sm:text-4xl lg:text-5xl">
                Featured collections
              </h2>
            </div>
            <Link to="/shop" className="group flex items-center gap-2 text-sm text-sage transition-colors hover:text-sage-deep" data-testid="featured-view-all">
              View everything
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
        {featured.length === 0 && (
          <p className="mt-12 text-center font-serif text-lg italic text-ink/50">
            The first collection is being pressed — check back shortly.
          </p>
        )}
      </section>

      {/* CATEGORIES BENTO */}
      <section className="bg-cream-dark/50 py-20 lg:py-28" data-testid="categories-bento">
        <div className="mx-auto max-w-7xl px-6 md:px-8">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sage">The shelves</p>
            <h2 className="mt-3 max-w-xl font-serif text-3xl font-normal tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Four quiet systems for a louder world
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12">
            {BENTO.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.08} className={c.cls}>
                <Link
                  to={`/shop?category=${c.id}`}
                  data-testid={c.testid}
                  className="group relative block h-full w-full overflow-hidden rounded-[1.6rem] border border-sage/10"
                >
                  <img
                    src={c.img}
                    alt={CATEGORY_LABELS[c.id]}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1.2s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/55 via-ink/5 to-transparent" />
                  <div className="absolute bottom-0 left-0 flex w-full items-center justify-between p-6">
                    <span className="font-serif text-2xl text-cream">{CATEGORY_LABELS[c.id]}</span>
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-cream/15 text-cream backdrop-blur transition-all duration-500 group-hover:bg-cream group-hover:text-sage">
                      <ArrowUpRight size={18} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-6 py-20 md:px-8 lg:py-28">
        <Reveal>
          <h2 className="text-center font-serif text-3xl font-normal tracking-tight text-ink sm:text-4xl">
            From our desk to yours
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.12}>
              <div className="h-full rounded-[1.6rem] border border-sage/12 bg-white/70 p-8 backdrop-blur transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(59,87,75,0.18)]">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sage-tint text-sage">
                  <s.icon size={20} />
                </span>
                <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-lavender-deep">
                  Step {i + 1}
                </p>
                <h3 className="mt-2 font-serif text-2xl text-ink">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ink/60">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA BAND */}
      <section className="px-6 pb-24 md:px-8">
        <Reveal>
          <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[2rem] bg-sage-deep px-8 py-16 text-center md:py-24">
            <div className="pointer-events-none absolute -left-20 -top-20 h-64 w-64 rounded-full bg-lavender/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -right-16 h-72 w-72 rounded-full bg-sage-light/25 blur-3xl" />
            <p className="relative font-mono text-[11px] uppercase tracking-[0.3em] text-lavender-light">
              Begin today
            </p>
            <h2 className="relative mx-auto mt-4 max-w-2xl font-serif text-3xl font-light leading-snug text-cream sm:text-4xl lg:text-5xl">
              A quieter way to organise everything that matters
            </h2>
            <Link
              to="/shop"
              data-testid="cta-band-shop-button"
              className="relative mt-10 inline-flex items-center gap-3 rounded-full bg-cream px-9 py-4 text-sm font-medium tracking-wide text-sage-deep transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_35px_-12px_rgba(0,0,0,0.4)]"
            >
              Browse the Shop <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
